import { useState } from 'react';
import {
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { PHONE_REGEX } from 'config/constants';
import { useCompany } from 'contexts/CompanyContext';
import { useFormik } from 'formik';
import { useProvider } from 'contexts/ProviderContext';
import * as Yup from 'yup';
import connexion from '_assets/parameters/connexion.svg';
import ControlPointIcon from '@material-ui/icons/ControlPoint';
import cniLogo from '_assets/parameters/cni-logo.svg';
import DefaultButton from 'components/DefaultButton';
import DefaultMenuItem from 'components/DefaultMenuItem';
import DocumentCard from 'components/DocumentCard';
import documents from '_assets/parameters/documents.svg';
import moment from 'moment';
import profile from '_assets/parameters/profile.svg';
import separator from '_assets/parameters/separator.svg';
import useStyles from './styles';
import info from '_assets/parameters/info.svg';
import eye from '_assets/parameters/eyeThin.svg';
import crossedEye from '_assets/parameters/eyeCrossed.svg';

const validationSchema = Yup.object({
  birthday: Yup.string(),
  city: Yup
    .string()
    .required('Champ obligatoire'),
  country: Yup
    .string()
    .required('Champ obligatoire'),
  email: Yup
    .string()
    .email('Veuillez saisir une adresse mail valide')
    .required('Champ obligatoire'),
  gender: Yup.string(),
  first_name: Yup
    .string()
    .required('Champ obligatoire'),
  last_name: Yup
    .string()
    .required('Champ obligatoire'),
  password: Yup
    .string()
    .min(6, 'Le mot de passe doit contenir au moins 6 caractères'),
  phone: Yup
    .string()
    .matches(PHONE_REGEX, 'Veuillez saisir un numéro de téléphone valide')
});

function SecurityParamsForm({ onDocumentChange, onPictureChange, onSubmit }) {
  const classes = useStyles();
  const { currentCompany } = useCompany();
  const { profilePicture, provider } = useProvider();
  const {
    errors,
    handleChange,
    handleSubmit,
    isSubmitting,
    resetForm,
    setFieldValue,
    touched,
    values
  } = useFormik({
    initialValues: {
      birthday: provider?.birthday ? moment(provider.birthday).format('YYYY-MM-DD') : '1980-01-01',
      city: provider?.addressEntity?.city_name ?? '',
      country: provider?.country ?? 'France',
      email: provider?.email ?? '',
      gender: provider?.sexe ?? 'M',
      first_name: provider?.first_name ?? '',
      last_name: provider?.last_name ?? '',
      password: '',
      phone: provider?.phone ?? '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const updated = await onSubmit(values);
      if (updated) {
        setEditConnexion(false);
        setEditInfo(false);
      }
      await setFieldValue('password', '');
    }
  });

  const [editConnexion, setEditConnexion] = useState(false);
  const [editInfo, setEditInfo] = useState(false);
  const [document, setDocument] = useState({ file: null, url: currentCompany?.identityURL ?? null });
  const [isUpdatingDocument, setIsUpdatingDocument] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const onCancelFormClick = () => {
    if (isSubmitting) return;

    setEditInfo(false);
    setEditConnexion(false);
    setDocument({ file: null, url: currentCompany?.identityURL ?? null });
    resetForm();
  }

  const handleDocumentChange = async (file, url) => {
    setIsUpdatingDocument(true);
    const updated = await onDocumentChange({ file, url });
    setDocument({ file: null, url: updated ? url : currentCompany?.identityURL ?? null });
    setIsUpdatingDocument(false);
  }

  return (
    <form className={classes.root} onSubmit={handleSubmit}>
      <Grid item xs={5}>
        <Grid container alignItems="center">
          <Grid item xs={4}>
            <img src={connexion} alt="Connexion logo" />
          </Grid>
          <Grid item xs={8}>
            <Typography variant="h3">Connexion</Typography>
          </Grid>
        </Grid>

        <Grid container item justify="flex-end">
          <DefaultButton
            className={classes.controlButton}
            color='primary'
            onClick={() => setEditConnexion(!editConnexion)}
          >
            Modifier
          </DefaultButton>
        </Grid>

        <Typography className={classes.subtitles}>Adresse e-mail</Typography>

        <TextField
          className={classes.leftTextfields}
          disabled
          name='email'
          variant='outlined'
          value={values.email}
          onChange={handleChange}
        />

        <Typography className={classes.subtitles}>Nouveau mot de passe</Typography>

        <div>
          <TextField
            className={classes.leftTextfields}
            disabled={!editConnexion}
            name='password'
            type={showPassword ? 'text' : 'password'}
            variant='outlined'
            value={values.password}
            onChange={handleChange}
            error={touched.password && Boolean(errors.password)}
            helperText={touched.password && errors.password}
            InputProps={{
              endAdornment: (
                <IconButton
                  disabled={!editConnexion}
                  onClick={() => setShowPassword(!showPassword)}
                  color={
                    touched.password && Boolean(errors.password)
                      ? "secondary"
                      : "primary"
                  }
                >
                  {showPassword ? <img src={eye} alt='Password visible logo' /> : <img src={crossedEye} alt='Password hidden logo'/>}
                </IconButton>
              ),
              autoComplete: 'new-password',
            }}
          />
        </div>

        <Typography className={classes.subtitles}>Téléphone mobile</Typography>

        <TextField
          className={classes.leftTextfields}
          disabled={!editConnexion}
          name='phone'
          variant='outlined'
          value={values.phone}
          onChange={handleChange}
          error={touched.phone && Boolean(errors.phone)}
          helperText={touched.phone && errors.phone}
        />
        <Grid style={{display:'flex', justifyContent:'center', alignItems:'center'}}>
          <DocumentCard
              disabled={isUpdatingDocument}
              document={document}
              logo={cniLogo}
              name="Carte d'identité"
              onChange={handleDocumentChange}
          />
        </Grid>
      </Grid>

      <img
        src={separator}
        alt="Bank logo"
        style={{ paddingTop:'7.5em', marginRight: "3em", marginTop: "0rem", marginBottom: "0rem" }}
      />

      <Grid item xs={6}>
        <Grid container alignItems="center">
          <Grid item xs={3}>
            <img src={documents} alt="Info Logo" />
          </Grid>
          <Grid item xs={9}>
            <Typography variant="h3">Informations du gestionnaire</Typography>
          </Grid>
        </Grid>

        <Grid container item xs={12} justify="flex-end">
          <DefaultButton
            className={classes.controlButton}
            color='primary'
            onClick={() => setEditInfo(!editInfo)}
          >
            Modifier
          </DefaultButton>
        </Grid>

        <Grid container xs={12}>
          <Grid item xs={6}>
            <Typography className={classes.subtitles}>Prénom</Typography>

            <TextField
              className={classes.rightTextfields}
              disabled={!editInfo}
              name='first_name'
              variant='outlined'
              value={values.first_name}
              onChange={handleChange}
              error={touched.first_name && Boolean(errors.first_name)}
              helperText={touched.first_name && errors.first_name}
            />

            <Typography className={classes.subtitles}>Date de naissance</Typography>

            <TextField
              className={classes.rightTextfields}
              disabled={!editInfo}
              fullWidth
              name='birthday'
              type='date'
              variant='outlined'
              value={values.birthday}
              onChange={handleChange}
              error={touched.birthday && Boolean(errors.birthday)}
              helperText={touched.birthday && errors.birthday}
            />

            <Typography className={classes.subtitles}>Ville</Typography>

            <TextField
              className={classes.rightTextfields}
              disabled={!editInfo}
              name='city'
              variant='outlined'
              value={values.city}
              onChange={handleChange}
              error={touched.city && Boolean(errors.city)}
              helperText={touched.city && errors.city}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography className={classes.subtitles}>Nom</Typography>

            <TextField
              className={classes.rightTextfields}
              disabled={!editInfo}
              name='last_name'
              variant='outlined'
              value={values.last_name}
              onChange={handleChange}
              error={touched.last_name && Boolean(errors.last_name)}
              helperText={touched.last_name && errors.last_name}
            />

            <Typography className={classes.subtitles}>Genre</Typography>

            <FormControl
              className={classes.rightTextfields}
              fullWidth
              variant="outlined"
            >
              <Select
                disabled={!editInfo}
                value={values.gender}
                onChange={handleChange}
              >
                <DefaultMenuItem value='M'>Homme</DefaultMenuItem>
                <DefaultMenuItem value='F'>Femme</DefaultMenuItem>
              </Select>
            </FormControl>

            <Typography className={classes.subtitles}>Pays</Typography>

            <TextField
              className={classes.rightTextfields}
              disabled={!editInfo}
              name='country'
              variant='outlined'
              value={values.country}
              onChange={handleChange}
              error={touched.country && Boolean(errors.country)}
              helperText={touched.country && errors.country}
            />
          </Grid>
        </Grid>

        <Grid container xs={12} style={{ marginTop: "3em" }}>
          <Grid size={2}>
            {
              <img
                id="loadedProfilePictureImg"
                src={profilePicture ?? profile}
                alt="profile"
                width="120px"
                height="122px"
              />
            }
          </Grid>
          <Grid item xs={6}>
            <input
              accept="image/*"
              onChange={onPictureChange}
              className={classes.input}
              id="profilePictureInput"
              name="profilePicture"
              type="file"
            />
            <label htmlFor="profilePictureInput">
              <IconButton
                aria-label='upload'
                color='secondary'
                component='span'
              >
                <ControlPointIcon fontSize='medium'/>
              </IconButton>
            </label>
            <Typography variant='body2' style={{ width: "90%", marginLeft: "10px" }}>
              Vérifiez que l’image soit bien au format PNG ou JPG et qu’elle ne
              dépasse pas 5 Mo
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      <Grid container item xs={12} justifyContent='flex-start' className={classes.infoText}>
        <img src={info} alt='Info logo' height={25} width={25} style={{marginRight:'0.5em'}}/>
        <Typography style={{lineHeight:'21.09px', fontSize:'18px' }}>L’ensemble des communications vous seront transmises sur votre adresse e-mail</Typography>
      </Grid>

      <Grid container item xs={12} justifyContent='center' style={{marginRight: "15em"}}>
        <Grid container item xs={12} alignItems='flex-end' justifyContent='center' style={{ marginBottom: '16px' }}>
          {(editConnexion || editInfo) && (
            <>
              <DefaultButton cancel onClick={onCancelFormClick}>
                Annuler
              </DefaultButton>
              <DefaultButton
                color='secondary'
                disabled={isSubmitting}
                variant='contained'
                type='submit'
              >
                {isSubmitting ? <CircularProgress size={20} /> : 'Valider'}
              </DefaultButton>
            </>
          )}
        </Grid>
      </Grid>
    </form>
  )
}

export default SecurityParamsForm;
